import { paths } from 'src/routes/paths';

import axios from 'src/utils/axios';

// ----------------------------------------------------------------------

function jwtDecode(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );

  return JSON.parse(jsonPayload);
}

// ----------------------------------------------------------------------

export const isValidToken = (accessToken: string) => {
  if (!accessToken) {
    return false;
  }

  const decoded = jwtDecode(accessToken);

  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

// ----------------------------------------------------------------------

// Global timer reference for token expiration
let expiredTimer: ReturnType<typeof setTimeout> | null = null;

export const tokenExpired = (exp: number) => {
  const currentTime = Date.now();
  
  // Calculate time left until expiration
  // Subtract 30 seconds as grace period to handle clock skew between client/server
  const GRACE_PERIOD = 30 * 1000; // 30 seconds
  const timeLeft = Math.max(0, exp * 1000 - currentTime - GRACE_PERIOD);

  // Clear any existing timer
  if (expiredTimer) {
    clearTimeout(expiredTimer);
  }

  // Set new expiration timer
  expiredTimer = setTimeout(() => {
    sessionStorage.removeItem('accessToken');
    delete axios.defaults.headers.common.Authorization;

    // Redirect to login if not already there
    if (window.location.pathname !== paths.auth.jwt.login) {
      window.location.href = paths.auth.jwt.login;
    }
  }, timeLeft);
};

// Export function to manually clear expiration timer (for logout)
export const clearTokenExpiredTimer = () => {
  if (expiredTimer) {
    clearTimeout(expiredTimer);
    expiredTimer = null;
  }
};

// ----------------------------------------------------------------------

export const setSession = (accessToken: string | null) => {
  if (accessToken) {
    sessionStorage.setItem('accessToken', accessToken);

    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    // This function below will handle when token is expired
    const { exp } = jwtDecode(accessToken); // ~3 days by minimals server
    tokenExpired(exp);
  } else {
    sessionStorage.removeItem('accessToken');
    clearTokenExpiredTimer();

    delete axios.defaults.headers.common.Authorization;
  }
};
